import type { AnonymityMode } from '@voice2me/shared';

const acknowledgeLabels: Record<AnonymityMode, string> = {
  anonymous: 'I understand my answers are anonymous',
  identified: 'I understand my answers are linked to my name',
  opt_in: 'I understand',
};

const bannerClass: Record<AnonymityMode, string> = {
  anonymous: 'banner banner-anonymous',
  identified: 'banner banner-identified',
  opt_in: 'banner banner-optin',
};

/**
 * Always shown before any answer is given. An employee deciding how candid to be is entitled to
 * know exactly what will be stored, so this states the mode plainly rather than in small print.
 *
 * When `onAcknowledgeChange` is supplied the banner shows an "I understand" checkbox that the
 * respondent must tick before they can proceed — the caller gates the survey on `acknowledged`.
 *
 * A survey may supply its own `agreement` text (a confidentiality note, participation terms, …).
 * When present it replaces the default anonymity wording in this banner, but the acknowledgement
 * and opt-in checkboxes stay, so the survey is still gated exactly as before.
 */
export function AnonymityBanner({
  mode,
  consent,
  onConsentChange,
  acknowledged,
  onAcknowledgeChange,
  agreement,
}: {
  mode: AnonymityMode;
  consent?: boolean;
  onConsentChange?: (consent: boolean) => void;
  acknowledged?: boolean;
  onAcknowledgeChange?: (acknowledged: boolean) => void;
  agreement?: string;
}) {
  const understand = onAcknowledgeChange ? (
    <label className="option banner-ack">
      <input
        type="checkbox"
        checked={acknowledged ?? false}
        onChange={(event) => onAcknowledgeChange(event.target.checked)}
      />
      <span>{acknowledgeLabels[mode]}</span>
    </label>
  ) : null;

  // The opt-in consent checkbox is the whole point of opt_in mode, so it survives a custom agreement.
  const optInConsent =
    mode === 'opt_in' ? (
      <label className="option">
        <input
          type="checkbox"
          checked={consent ?? false}
          onChange={(event) => onConsentChange?.(event.target.checked)}
        />
        <span>Attach my name to my answers</span>
      </label>
    ) : null;

  // A custom agreement replaces the default per-mode wording but keeps the gating checkboxes.
  const custom = agreement?.trim();
  if (custom) {
    return (
      <div className={bannerClass[mode]}>
        <p className="banner-agreement">{custom}</p>
        {optInConsent}
        {understand}
      </div>
    );
  }

  if (mode === 'anonymous') {
    return (
      <div className="banner banner-anonymous">
        <strong>Your answers are anonymous.</strong>
        <p>
          Your employer will be able to see <em>that</em> you took part, but your answers are stored
          with no link to you.
        </p>
        {understand}
      </div>
    );
  }

  if (mode === 'identified') {
    return (
      <div className="banner banner-identified">
        <strong>Your answers are linked to your name.</strong>
        <p>Whoever reviews this survey will be able to see that these are your answers.</p>
        {understand}
      </div>
    );
  }

  return (
    <div className="banner banner-optin">
      <strong>You choose whether your answers are linked to you.</strong>
      <p>
        By default your answers are anonymous. Tick the box below if you are happy for them to be
        attributed to you, for example so someone can follow up with you directly.
      </p>
      {optInConsent}
      {understand}
    </div>
  );
}
